import React, { createContext, useContext, useState, useEffect } from 'react';
import { initializeSession, getSessionStartTime } from '../helpers/auth-helper';

const AuthContext = createContext();

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  const [authData, setAuthData] = useState(null);
  const [otpMethod, setOtpMethod] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [isImpersonating, setIsImpersonating] = useState(false);
  const [impersonatedUser, setImpersonatedUser] = useState(null);

  // Initialize auth data from session storage on mount
  useEffect(() => {
    const token = sessionStorage.getItem('token');
    const userId = sessionStorage.getItem('userId');
    const userType = sessionStorage.getItem('roleName');
    const mobileNumber = sessionStorage.getItem('mobile_number');
    const email = sessionStorage.getItem('email');
    const deliveryReportPermission = sessionStorage.getItem('deliveryReportPermission');
    const downloadReportPermission = sessionStorage.getItem('downloadReportPermission');
    const isImpersonatingStored = sessionStorage.getItem('isImpersonating') === 'true';

    //console.log('🔍 AuthContext initializing - isImpersonating:', isImpersonatingStored);

    if (token && userId) {
      setAuthData({
        token,
        user_id: userId,
        user_type: userType,
        mobile_number: mobileNumber,
        email: email,
        deliveryReportPermission: deliveryReportPermission,
        downloadReportPermission: downloadReportPermission
      });
      
      // Restore impersonation state if exists
      if (isImpersonatingStored) {
        const impersonatedUserId = sessionStorage.getItem('impersonatedUserId');
        const impersonatedUserName = sessionStorage.getItem('impersonatedUserName');
        const impersonatedUserEmail = sessionStorage.getItem('impersonatedUserEmail');
        
        // console.log('👤 Restoring impersonation state:', {
        //   userId: impersonatedUserId,
        //   username: impersonatedUserName,
        //   email: impersonatedUserEmail
        // });
        
        setIsImpersonating(true);
        setImpersonatedUser({
          user_id: impersonatedUserId,
          username: impersonatedUserName,
          email: impersonatedUserEmail
        });
      }
      
      // Initialize session timing if not already set
      if (!getSessionStartTime()) {
        initializeSession();
      }
    }
  }, []);

  const login = (userData) => {
    setAuthData(userData);
    // Also store in session storage for persistence
    sessionStorage.setItem('token', userData.token);
    sessionStorage.setItem('userId', userData.user_id);
    sessionStorage.setItem('roleName', userData.user_type);
    sessionStorage.setItem('mobile_number', userData.mobile_number);
    sessionStorage.setItem('email', userData.email);
    sessionStorage.setItem('deliveryReportPermission', userData.deliveryReportPermission);
    sessionStorage.setItem('downloadReportPermission', userData.downloadReportPermission);
    
    // Initialize session timing
    initializeSession();
  };

  const logout = () => {
    setAuthData(null);
    setOtpMethod(null);
    // Clear session storage
    sessionStorage.removeItem('token');
    sessionStorage.removeItem('userId');
    sessionStorage.removeItem('roleName');
    sessionStorage.removeItem('mobile_number');
    sessionStorage.removeItem('email');
    sessionStorage.removeItem('selectedOtpMethod');
    sessionStorage.removeItem('deliveryReportPermission');
    sessionStorage.removeItem('downloadReportPermission');
  };

  const setOtpVerificationMethod = (method) => {
    setOtpMethod(method);
    sessionStorage.setItem('selectedOtpMethod', method);
  };

  const clearOtpMethod = () => {
    setOtpMethod(null);
    sessionStorage.removeItem('selectedOtpMethod');
  };

  const startImpersonation = (userData) => {
    //console.log('🔄 Starting impersonation with userData:', userData);
    
    // Save admin session before switching
    const adminSession = {
      token: sessionStorage.getItem('token'),
      userId: sessionStorage.getItem('userId'),
      roleName: sessionStorage.getItem('roleName'),
      mobile_number: sessionStorage.getItem('mobile_number'),
      email: sessionStorage.getItem('email'),
      sessionStartTime: sessionStorage.getItem('sessionStartTime'),
      lastActivityTime: sessionStorage.getItem('lastActivityTime'),
      deliveryReportPermission: sessionStorage.getItem('deliveryReportPermission'),
      downloadReportPermission: sessionStorage.getItem('downloadReportPermission')
    };
    
    //console.log('💾 Saved admin session:', adminSession);
    sessionStorage.setItem('adminSession', JSON.stringify(adminSession));
    
    // Switch to impersonated user
    sessionStorage.setItem('token', userData.token);
    sessionStorage.setItem('userId', userData.user_id);
    sessionStorage.setItem('roleName', userData.user_type);
    sessionStorage.setItem('mobile_number', userData.mobile_number || '');
    sessionStorage.setItem('email', userData.email || '');
    sessionStorage.setItem('deliveryReportPermission', userData.deliveryReportPermission || '');
    sessionStorage.setItem('downloadReportPermission', userData.downloadReportPermission || '');
    sessionStorage.setItem('isImpersonating', 'true');
    sessionStorage.setItem('impersonatedUserId', userData.user_id);
    sessionStorage.setItem('impersonatedUserName', userData.username || userData.user_id);
    sessionStorage.setItem('impersonatedUserEmail', userData.email || '');
    
    // Update state
    setAuthData({
      token: userData.token,
      user_id: userData.user_id,
      user_type: userData.user_type,
      mobile_number: userData.mobile_number,
      email: userData.email,
      deliveryReportPermission: userData.deliveryReportPermission,
      downloadReportPermission: userData.downloadReportPermission
    });
    
    setImpersonatedUser({
      user_id: userData.user_id,
      username: userData.username || userData.user_id,
      email: userData.email
    });
    
    setIsImpersonating(true);
    
    //console.log('✅ Impersonation state set - isImpersonating: true, user:', userData.username || userData.user_id);
    
    // Initialize new session timing for impersonated user
    initializeSession();
    
    // Navigate to user's dashboard
    window.location.href = '/dashboard';
  };

  const stopImpersonation = () => {
    const adminSessionStr = sessionStorage.getItem('adminSession');
    if (!adminSessionStr) {
      console.error('No admin session found');
      return;
    }
    
    const adminSession = JSON.parse(adminSessionStr);
    
    // Restore admin session
    sessionStorage.setItem('token', adminSession.token);
    sessionStorage.setItem('userId', adminSession.userId);
    sessionStorage.setItem('roleName', adminSession.roleName);
    sessionStorage.setItem('mobile_number', adminSession.mobile_number);
    sessionStorage.setItem('email', adminSession.email);
    sessionStorage.setItem('sessionStartTime', adminSession.sessionStartTime);
    sessionStorage.setItem('lastActivityTime', adminSession.lastActivityTime);
    sessionStorage.setItem('deliveryReportPermission', adminSession.deliveryReportPermission || '');
    sessionStorage.setItem('downloadReportPermission', adminSession.downloadReportPermission || '');
    
    // Clear impersonation flags
    sessionStorage.removeItem('isImpersonating');
    sessionStorage.removeItem('impersonatedUserId');
    sessionStorage.removeItem('impersonatedUserName');
    sessionStorage.removeItem('impersonatedUserEmail');
    sessionStorage.removeItem('adminSession');
    
    // Update state
    setAuthData({
      token: adminSession.token,
      user_id: adminSession.userId,
      user_type: adminSession.roleName,
      mobile_number: adminSession.mobile_number,
      email: adminSession.email,
      deliveryReportPermission: adminSession.deliveryReportPermission,
      downloadReportPermission: adminSession.downloadReportPermission
    });
    
    setIsImpersonating(false);
    setImpersonatedUser(null);
    
    // Navigate back to admin dashboard
    window.location.href = '/AdminDashboard';
  };

  const value = {
    authData,
    otpMethod,
    isLoading,
    isImpersonating,
    impersonatedUser,
    setIsLoading,
    login,
    logout,
    setOtpVerificationMethod,
    clearOtpMethod,
    startImpersonation,
    stopImpersonation
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};
